import React, {
  FC,
  useState,
  useCallback,
  useEffect,
  Dispatch,
  SetStateAction,
} from 'react';

import dropdownArrow from 'assets/img/dropdown-arrow.svg';

import {
  Container,
  ListContainer,
  LayerTouchable,
  ListItem,
  AnimatedArrow,
} from './styles';

interface DataDTO {
  label: string;
  value: string | number;
}
interface DropdownSelectorProps {
  placeholder: string;
  data: DataDTO[];
  isSmall?: boolean;
  width?: number;
  fillWidth?: boolean;
  initialValue?: any;
  bolsaInitialId?: number;
  setFatherState?:
    | Dispatch<SetStateAction<string>>
    | Dispatch<SetStateAction<number>>;
}

const SPACEBAR_KEY_CODE = [0, 32];
const ENTER_KEY_CODE = 13;
const DOWN_ARROW_KEY_CODE = 40;
const UP_ARROW_KEY_CODE = 38;
const ESCAPE_KEY_CODE = 27;

const DropdownSelector: FC<DropdownSelectorProps> = ({
  placeholder,
  data,
  isSmall,
  width,
  fillWidth,
  setFatherState,
  initialValue,
  bolsaInitialId,
}) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedValue, setSelectedValue] = useState<string | null>(null);
  const [hoveredValue, setHoveredValue] = useState<number | null>(null);
  const [lastKeyPressed, setLastKeyPressed] = useState<number | null>(null);

  const handleSelectItem = useCallback((item) => {
    setSelectedValue(item.label);

    if (setFatherState) setFatherState(item.value);
  }, []);

  useEffect(() => {
    if (initialValue) {
      const filteredInitialData = data.filter(
        (item) => item.value === String(initialValue),
      );

      if (filteredInitialData.length > 0) {
        setSelectedValue(filteredInitialData[0].label);
        if (setFatherState) setFatherState(filteredInitialData[0].value as any);
      } else if (setFatherState) {
        const label = `${initialValue.slice(0, -1)}/${initialValue.slice(-1)}`;

        setSelectedValue(label);
        setFatherState(initialValue);
      }
    }

    if (bolsaInitialId) {
      const [initialBolsaData] = data.filter(
        (item) => item.value === bolsaInitialId,
      );

      setSelectedValue(initialBolsaData.label);
      if (setFatherState) setFatherState(initialBolsaData.value as any);
    }
  }, []);

  useEffect(() => {
    switch (Number(lastKeyPressed?.toFixed(0))) {
      case UP_ARROW_KEY_CODE: {
        if (showDropdown) {
          if (hoveredValue !== null && hoveredValue > 0) {
            setHoveredValue(hoveredValue - 1);
          } else {
            setHoveredValue(data.length - 1);
          }
        }

        break;
      }

      case DOWN_ARROW_KEY_CODE: {
        if (showDropdown) {
          if (hoveredValue !== null && hoveredValue < data.length - 1) {
            setHoveredValue(hoveredValue + 1);
          } else {
            setHoveredValue(0);
          }
        } else {
          setHoveredValue(null);
        }

        break;
      }

      case ESCAPE_KEY_CODE:
        setShowDropdown(false);
        break;

      case ENTER_KEY_CODE:
        if (hoveredValue) {
          handleSelectItem(data[hoveredValue]);
          setShowDropdown(false);
        }
        break;

      case SPACEBAR_KEY_CODE[0] || SPACEBAR_KEY_CODE[1]:
        setShowDropdown(false);
        break;

      default:
        break;
    }
  }, [lastKeyPressed]);

  const handleKeyboardOnPress = useCallback((e: globalThis.KeyboardEvent) => {
    setLastKeyPressed((currValue) =>
      currValue === e.keyCode ? e.keyCode + 0.1 : e.keyCode,
    );
  }, []);

  useEffect(() => {
    document.addEventListener('keydown', (e) => handleKeyboardOnPress(e));

    return () => {
      document.removeEventListener('keydown', (e) => handleKeyboardOnPress(e));
    };
  }, [handleKeyboardOnPress]);

  useEffect(() => {
    if (!showDropdown) {
      setHoveredValue(null);
    }
  }, [showDropdown]);

  return (
    <>
      {showDropdown && (
        <LayerTouchable onClick={(): void => setShowDropdown(false)} />
      )}
      <Container
        onClick={(): void => setShowDropdown(!showDropdown)}
        isSmall={isSmall}
        width={width}
        fillWidth={fillWidth}
      >
        <span>{selectedValue || placeholder}</span>
        <AnimatedArrow
          state={showDropdown}
          src={dropdownArrow}
          alt="dropdown-arrow"
        />

        {showDropdown && (
          <ListContainer role="listbox" isSmall={isSmall}>
            {data.map((item: DataDTO, index: number) => (
              <ListItem
                isHovered={hoveredValue === index}
                isSelected={item.label === selectedValue}
                aria-selected={item.label === selectedValue}
                role="option"
                key={item.value}
                tabIndex={0}
                onClick={(): void => handleSelectItem(item)}
              >
                {item.label}
              </ListItem>
            ))}
          </ListContainer>
        )}
      </Container>
    </>
  );
};

export default DropdownSelector;
